import React, { useState, useEffect, useRef } from 'react';

/**
 * Excel-style column filter dropdown.
 * Props:
 *   label      – column header label
 *   options    – array of { value, label } objects (all unique values)
 *   selected   – Set of selected values (empty Set = all selected)
 *   onChange   – fn(newSet) called when selection changes
 *   width      – optional min-width of dropdown (default 200)
 */
export default function ExcelFilter({ label, options, selected, onChange, width = 200 }) {
  const [open, setOpen]     = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef();

  // Close on outside click
  useEffect(() => {
    function handle(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
    if (open) document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [open]);

  const allSelected = selected.size === 0;
  const filtered    = options.filter(o =>
    String(o.label).toLowerCase().includes(search.toLowerCase())
  );

  function toggleAll() {
    onChange(new Set()); // empty = all selected
  }

  function toggle(val) {
    const next = new Set(selected);
    if (next.has(val)) {
      next.delete(val);
      if (next.size === 0) onChange(new Set()); // if nothing left, reset to all
      else onChange(next);
    } else {
      // If currently "all", start fresh with just this one UNselected logic:
      // Actually Excel behaviour: if all selected and you click one, select ONLY that one
      if (allSelected) {
        onChange(new Set([val]));
      } else {
        next.add(val);
        if (next.size === options.length) onChange(new Set()); // all checked → reset to all
        else onChange(next);
      }
    }
  }

  const isActive = !allSelected;
  const activeCount = selected.size;

  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-block' }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          display: 'flex', alignItems: 'center', gap: 4,
          padding: '4px 8px', border: isActive ? '1.5px solid #4A7A91' : '1px solid #d1d5db',
          borderRadius: 0, background: isActive ? '#e8f2f7' : 'white',
          cursor: 'pointer', fontSize: 12, fontWeight: isActive ? 700 : 500,
          color: isActive ? '#1d6fa4' : '#374151', whiteSpace: 'nowrap',
        }}
      >
        <span>{label}</span>
        {isActive && (
          <span style={{
            background: '#4A7A91', color: 'white', borderRadius: 0,
            fontSize: 10, padding: '0 5px', fontWeight: 700,
          }}>{activeCount}</span>
        )}
        <span style={{ fontSize: 9, color: '#9ca3af' }}>▼</span>
      </button>

      {open && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, zIndex: 200, marginTop: 4,
          background: 'white', border: '1.5px solid #e5e7eb', borderRadius: 0,
          boxShadow: '0 4px 16px rgba(0,0,0,0.12)', minWidth: width,
        }}>
          {/* Search */}
          <div style={{ padding: '8px 8px 4px' }}>
            <input
              autoFocus
              placeholder="Search..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{
                width: '100%', padding: '5px 8px', border: '1px solid #e5e7eb',
                borderRadius: 0, fontSize: 12, outline: 'none', boxSizing: 'border-box',
              }}
            />
          </div>

          {/* Select All */}
          <div style={{ padding: '4px 8px', borderBottom: '1px solid #f3f4f6' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
              <input
                type="checkbox"
                checked={allSelected}
                onChange={toggleAll}
                style={{ cursor: 'pointer' }}
              />
              (Select All)
            </label>
          </div>

          {/* Options list */}
          <div style={{ maxHeight: 220, overflowY: 'auto', padding: '4px 0' }}>
            {filtered.map(o => {
              const checked = allSelected || selected.has(o.value);
              return (
                <label
                  key={o.value}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    padding: '4px 12px', cursor: 'pointer', fontSize: 12,
                    background: selected.has(o.value) && !allSelected ? '#f0f9ff' : 'transparent',
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = '#f9fafb'}
                  onMouseLeave={e => e.currentTarget.style.background = selected.has(o.value) && !allSelected ? '#f0f9ff' : 'transparent'}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggle(o.value)}
                    style={{ cursor: 'pointer' }}
                  />
                  <span>{o.label}</span>
                </label>
              );
            })}
            {filtered.length === 0 && (
              <div style={{ padding: '8px 12px', color: '#9ca3af', fontSize: 12 }}>No results</div>
            )}
          </div>

          {/* Footer */}
          <div style={{ padding: '6px 8px', borderTop: '1px solid #f3f4f6', display: 'flex', justifyContent: 'flex-end', gap: 6 }}>
            <button
              onClick={() => { onChange(new Set()); setOpen(false); }}
              style={{ padding: '3px 10px', border: '1px solid #e5e7eb', borderRadius: 0, fontSize: 11, cursor: 'pointer', background: 'white' }}
            >Clear</button>
            <button
              onClick={() => setOpen(false)}
              style={{ padding: '3px 10px', border: 'none', borderRadius: 0, fontSize: 11, cursor: 'pointer', background: '#4A7A91', color: 'white', fontWeight: 600 }}
            >Apply</button>
          </div>
        </div>
      )}
    </div>
  );
}
